import type { ReactNode } from 'react';

/**
 * Touch targets are sized for the worst case: a guest at a busy screening,
 * one-handed, in the dark. Kiosk variants are bigger again.
 */
export function Button({
  children,
  onClick,
  variant = 'primary',
  kiosk = false,
  disabled = false,
  full = true,
  onArt = false,
  testId,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'ghost' | 'sand' | 'red';
  kiosk?: boolean;
  disabled?: boolean;
  full?: boolean;
  /**
   * Stable handle for the smoke test. Copy is campaign-owned and changes
   * without notice — keying the test off button text meant every copy pass
   * silently broke it, and it stayed broken across four commits.
   */
  testId?: string;
  /**
   * Sitting on the key art's cream ground rather than on ink. Straw is a dry
   * grass midtone sampled from the illustration, so a straw fill vanishes into
   * the parchment — on art the primary is the title red instead.
   *
   * `'lg'` is for screens whose ground changes at that breakpoint: dark on a
   * phone, art above it. The alternative was rendering the button twice behind
   * a `lg:hidden` pair, which duplicates every data-testid in the DOM and makes
   * a Playwright locator ambiguous.
   */
  onArt?: boolean | 'lg';
}) {
  // Hover is a slight fade rather than a colour shift, so one rule covers every
  // variant — a solid red fill, a sand fill, an outline and a text link all read
  // the same way. See tailwind.config.js for why it is gated on devices that can
  // actually hover.
  const base =
    'inline-flex items-center justify-center rounded-full font-body font-semibold uppercase tracking-wide transition-opacity hover:opacity-90 active:opacity-70 disabled:opacity-40 disabled:pointer-events-none';
  const size = kiosk ? 'min-h-[6rem] px-16 text-3xl' : 'min-h-[3.5rem] px-8 text-base';
  /**
   * `sand` is a solid fill in the title plate's sky colour, and unlike the other
   * three it does not care what ground it is on — the result screen's actions
   * use it over a dark phone layout and over the art on desktop alike.
   *
   * The hex is written out rather than read from TITLE_SKY because Tailwind can
   * only emit classes whose text it finds in the source. An arbitrary value like
   * this is generated from the class string with no config involvement, which is
   * deliberate: the same colour as a theme token needed a config reload to
   * appear, and a stale dev server silently dropped it. Keep in step with
   * TITLE_SKY in src/assets.ts.
   */
  const SAND = 'bg-[#E8E1C4] text-ink';

  /**
   * The title red. Like `sand`, ground-independent — it carries on the dark
   * phone layout and on the art alike, so the primary action reads the same
   * either side of the breakpoint. Safe as a theme token rather than an
   * arbitrary value: `blood` is an original palette key, so the class already
   * exists and a stale config would only mean the older, darker red.
   */
  const RED = 'bg-blood text-bone';

  const LOOKS = {
    ink: {
      primary: 'bg-straw text-ink',
      secondary: 'border-2 border-bone/40 text-bone',
      ghost: 'text-bone/60 underline underline-offset-4',
      sand: SAND,
      red: RED,
    },
    art: {
      primary: 'bg-blood text-bone',
      secondary: 'border-2 border-ink/40 text-ink',
      ghost: 'text-ink/60 underline underline-offset-4',
      sand: SAND,
      red: RED,
    },
    lg: {
      primary: 'bg-straw text-ink lg:bg-blood lg:text-bone',
      secondary: 'border-2 border-bone/40 text-bone lg:border-ink/40 lg:text-ink',
      ghost: 'text-bone/60 underline underline-offset-4 lg:text-ink/60',
      sand: SAND,
      red: RED,
    },
  } as const;
  const look = LOOKS[onArt === true ? 'art' : onArt === 'lg' ? 'lg' : 'ink'][variant];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={[base, size, look, full ? 'w-full' : ''].join(' ')}
    >
      {children}
    </button>
  );
}
